"use client";

import PhoneField from "@/components/auth/shared/PhoneField";
import FormInputField from "@/components/custom-form/FormInputField";
import AuthFooter from "@/components/auth/shared/AuthFooter";
import SocialAuthButtons from "@/components/auth/shared/SocialAuthButtons";
import AuthTabs from "../shared/AuthTabs";
import TabPanel from "@/components/auth/shared/TabPanel";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { IconEye, IconEyeClosed, IconLock, IconMail } from "@tabler/icons-react";

type LoginContentProps = {
  activeTab: "email" | "phone";
  phone: string;
  isSubmitting: boolean;
  errorMessage?: string;
  onTabChange: (value: "email" | "phone") => void;
  onPhoneChange: (value: string) => void;
  onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  onRegisterClick: () => void;
  onForgotPasswordClick: () => void;
};

const LoginContent = ({
  activeTab,
  phone,
  isSubmitting,
  onTabChange,
  onPhoneChange,
  onSubmit,
  onRegisterClick,
  onForgotPasswordClick,
}: LoginContentProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { watch, control } = useFormContext();
  const formEmail = watch("email");
  const formPassword = watch("password");

  const isLoginDisabled =
    activeTab === "email"
      ? !formEmail || !formPassword
      : !phone || phone.replace(/\D/g, "").length < 8 || !formPassword;

  return (
    <div className="mx-auto flex h-full w-full flex-col px-4 md:pb-0 pb-10">
      <AuthTabs value={activeTab} onChange={onTabChange} />

      <div className="flex flex-1 flex-col">
        <div>
          <form
            onSubmit={onSubmit}
            className="flex flex-col mt-4"
          >
            <div className="relative min-h-[88px] sm:min-h-[92px]">
              <TabPanel active={activeTab === "email"}>
                <FormInputField
                  control={control}
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="Email"
                  leadingIcon={<IconMail className="h-5 w-5" stroke={1.8} />}
                />
              </TabPanel>

              <TabPanel active={activeTab === "phone"}>
                <PhoneField value={phone} onChange={onPhoneChange} />
              </TabPanel>
            </div>

            <FormInputField
              control={control}
              name="password"
              type={showPassword ? "text" : "password"}
              label="Password"
              placeholder="Password"
              leadingIcon={<IconLock className="h-5 w-5" stroke={1.8} />}
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="flex items-center text-body hover:text-heading"
                >
                  {showPassword ? (
                    <IconEye className="h-5 w-5" stroke={1.8} />
                  ) : (
                    <IconEyeClosed className="h-5 w-5" stroke={1.8} />
                  )}
                </button>
              }
            />

            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={onForgotPasswordClick}
                className="text-base font-medium text-secondary hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              size="xl"
              disabled={isLoginDisabled || isSubmitting}
              isLoading={isSubmitting}
              className="my-4"
            >
              Sign In
            </Button>

            <div className="mb-0.5 flex items-center gap-3 sm:mb-1">
              <div className="h-px flex-1 bg-subtle" />
              <span className="text-sm text-caption">or</span>
              <div className="h-px flex-1 bg-subtle" />
            </div>
          </form>

          <SocialAuthButtons />
        </div>

        <AuthFooter
          prompt="Don't have an account?"
          actionLabel="Create a new account"
          onActionClick={onRegisterClick}
          className="pt-4 sm:pt-6"
        />
      </div>
    </div>
  );
};

export default LoginContent;
